import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

// Native ERP port of the standalone Sales Reporter app's "Reports" tab.
// Mirrors the actual api/get_my_summary.php + api/get_team_summary.php
// exactly: same period filters (daily/weekly/monthly), same fields, and
// — for Team Summary — the same manager-only access (Territory Managers
// see their own territory, General Managers see everyone) with
// zero-activity members still listed instead of dropped. See
// backend/sales_report.py's Phase 4 module docstring for the one
// deliberate adaptation this porting required (role/is_active mapped to
// this module's own opt-in sales_role, since `users` here is shared
// across every ERP module, not a dedicated sales-app table).
const PERIODS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const money = (n) => `₹ ${Number(n || 0).toLocaleString()}`;

function StatTile({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1 mono">{value}</div>
    </div>
  );
}

export default function SalesReportsPage() {
  const [period, setPeriod] = useState("daily");
  const [profile, setProfile] = useState(null);
  const [mySummary, setMySummary] = useState(null);
  const [teamSummary, setTeamSummary] = useState(null);
  const [tab, setTab] = useState("mine");
  const [loading, setLoading] = useState(false);

  const isManager = profile?.effective_sales_role === "tm" || profile?.effective_sales_role === "gm";

  useEffect(() => {
    api.get("/sales-report/me").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  const loadMine = async () => {
    const { data } = await api.get("/sales-report/my-summary", { params: { period } });
    setMySummary(data);
  };
  const loadTeam = async () => {
    try {
      const { data } = await api.get("/sales-report/team-summary", { params: { period } });
      setTeamSummary(data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadMine(), isManager ? loadTeam() : Promise.resolve()])
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, isManager]);

  const rangeLabel = useMemo(() => {
    if (!mySummary) return "";
    return mySummary.from === mySummary.to ? mySummary.from : `${mySummary.from} → ${mySummary.to}`;
  }, [mySummary]);

  return (
    <div className="space-y-6" data-testid="sales-reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Reports
          </h1>
          <p className="text-sm text-slate-500 mt-1">{rangeLabel}</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="report-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-my-summary">My Summary</TabsTrigger>
          {isManager && <TabsTrigger value="team" data-testid="tab-team-summary">Team Summary</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine" className="space-y-4">
          {mySummary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile label="Total Visits" value={mySummary.visits.total_visits} />
                <StatTile label="Customers Visited" value={mySummary.visits.customers_visited} />
                <StatTile label="Dealers Visited" value={mySummary.visits.dealers_visited} />
                <StatTile label="Quotations Sent" value={mySummary.visits.quotations_sent} />
                <StatTile label="Quotation Value" value={money(mySummary.visits.quotation_value)} />
                <StatTile label="Orders Received" value={mySummary.visits.orders_received} />
                <StatTile label="Order Value" value={money(mySummary.visits.order_value)} />
                <StatTile label="Payment Collected" value={money(mySummary.visits.payment_collected)} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile label="Total KM" value={Number(mySummary.expenses.total_km || 0)} />
                <StatTile label="Travel" value={money(mySummary.expenses.travel_amount)} />
                <StatTile label="Boarding" value={money(mySummary.expenses.boarding_amount)} />
                <StatTile label="Lodging" value={money(mySummary.expenses.lodging_amount)} />
                <StatTile label="Other" value={money(mySummary.expenses.other_amount)} />
                <StatTile label="Total Expenses" value={money(mySummary.expenses.total_expenses)} />
              </div>
            </>
          )}
          {!loading && !mySummary && (
            <div className="text-center text-slate-400 text-sm py-10">No data for this period.</div>
          )}
        </TabsContent>

        {isManager && (
          <TabsContent value="team" className="space-y-4">
            {teamSummary && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatTile label="Total Visits" value={teamSummary.totals.total_visits} />
                  <StatTile label="Quotations Sent" value={teamSummary.totals.quotations_sent} />
                  <StatTile label="Quotation Value" value={money(teamSummary.totals.quotation_value)} />
                  <StatTile label="Orders Received" value={teamSummary.totals.orders_received} />
                  <StatTile label="Order Value" value={money(teamSummary.totals.order_value)} />
                  <StatTile label="Total KM" value={Number(teamSummary.totals.total_km || 0)} />
                  <StatTile label="Total Expenses" value={money(teamSummary.totals.total_expenses)} />
                  <StatTile label="Members" value={teamSummary.members.length} />
                </div>

                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm" data-testid="team-summary-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-3 px-4">Territory</th>
                        <th className="py-3 px-4">Name</th>
                        <th className="py-3 px-4">Role</th>
                        <th className="py-3 px-4">Visits</th>
                        <th className="py-3 px-4">Quotations</th>
                        <th className="py-3 px-4">Quotation Value</th>
                        <th className="py-3 px-4">Orders</th>
                        <th className="py-3 px-4">Order Value</th>
                        <th className="py-3 px-4">KM</th>
                        <th className="py-3 px-4">Expenses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamSummary.members.map((m) => (
                        <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`team-row-${m.id}`}>
                          <td className="py-3 px-4 text-slate-600 text-xs">{m.territory || "—"}</td>
                          <td className="py-3 px-4 text-slate-800">{m.name}</td>
                          <td className="py-3 px-4 text-xs uppercase text-slate-500">{m.role}</td>
                          <td className="py-3 px-4 mono">{m.total_visits}</td>
                          <td className="py-3 px-4 mono">{m.quotations_sent}</td>
                          <td className="py-3 px-4 mono">{money(m.quotation_value)}</td>
                          <td className="py-3 px-4 mono">{m.orders_received}</td>
                          <td className="py-3 px-4 mono">{money(m.order_value)}</td>
                          <td className="py-3 px-4 mono">{Number(m.total_km || 0)}</td>
                          <td className="py-3 px-4 mono">{money(m.total_expenses)}</td>
                        </tr>
                      ))}
                      {teamSummary.members.length === 0 && (
                        <tr><td colSpan={10} className="py-10 text-center text-slate-500 text-sm">No opted-in team members yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
